// Branding configuration types — matches site_config.settings.branding JSON

export interface BrandingConfig {
  logoUrl?: string;       // URL to logo image (served from Edge or external)
  primaryColor?: string;  // Hex color, e.g. "#C5A258"
  accentColor?: string;   // Hex color for the brand accent (--brand-accent)
  fontFamily?: string;    // CSS font-family name
  darkDefault?: boolean;  // Default to dark mode
  customCss?: string;     // Future: inject custom CSS overrides
  loginBackground?: string; // Filename of a shipped image under /bg/, or unset for none
}

// The two candidates for text drawn on top of --primary. They match the
// --primary-foreground values in index.css: a warm near-black for light brand
// colours, near-white for dark ones.
const DARK_ON_PRIMARY = 'oklch(0.2 0.03 50)';
const LIGHT_ON_PRIMARY = 'oklch(0.985 0 0)';

function relativeLuminance(hex: string): number | null {
  const value = hex.replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;

  const channels = [0, 2, 4].map((offset) => {
    const c = parseInt(value.substring(offset, offset + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/**
 * Pick the readable foreground for an arbitrary branding primary.
 *
 * Compares the WCAG contrast of both candidates against the brand colour and
 * returns the winner, so the choice is measured rather than guessed. The Corteh
 * orange #F08341 lands on the dark foreground (6.96:1, against 2.62:1 for
 * white) — white on that orange was the worst pair in the 2026-09-06 audit.
 *
 * Returns null when the hex cannot be parsed, so the caller leaves the
 * stylesheet value in place.
 */
export function foregroundForPrimary(hex: string): string | null {
  const primary = relativeLuminance(hex);
  if (primary === null) return null;

  // Luminance of the two candidates, precomputed: oklch(0.2 0.03 50) ≈ 0.0166,
  // oklch(0.985 0 0) ≈ 0.9614.
  const contrastWithDark = (primary + 0.05) / (0.0166 + 0.05);
  const contrastWithLight = (0.9614 + 0.05) / (primary + 0.05);

  return contrastWithDark >= contrastWithLight ? DARK_ON_PRIMARY : LIGHT_ON_PRIMARY;
}
